import { useId, useState } from "react";
import { MenuItem as MuiMenuItem } from "@mui/material";
import { useForkRef } from "@mui/material/utils";

import Icons from "assets";
import { Box } from "ui/box/box";
import { mergeSx } from "ui/merge-sx";
import { ActionsMenu } from "ui/actions-menu/actions-menu";
import { ActionsMenuProvider, useActionsMenuClose } from "ui/actions-menu/actions-menu-context";
import { MenuItemContent } from "ui/actions-menu/menu-item/menu-item-content";
import { ITEM_SX } from "ui/actions-menu/menu-item/menu-item.styles";
import { SUBMENU_OFFSET } from "ui/actions-menu/submenu-item/submenu-item.constants";
import { CHEVRON_SX, WRAPPER_SX } from "ui/actions-menu/submenu-item/submenu-item.styles";
import {
  type SubmenuItemProps,
  type SubmenuItemTriggerProps,
} from "ui/actions-menu/submenu-item/submenu-item.types";

/**
 * A `MuiMenuItem` like `MenuItem`, so the enclosing `MenuList` keeps it in its arrow-key order. It
 * drops the `Actions for …` `aria-label` `ActionsMenu` injects, so the visible label stays the name.
 */
const SubmenuItemTrigger = ({
  icon,
  label,
  isSelected,
  sx,
  ref,
  ...rest
}: SubmenuItemTriggerProps) => {
  const itemRef = useForkRef(ref);

  return (
    <MuiMenuItem
      {...rest}
      aria-label={undefined}
      component="button"
      type="button"
      disableGutters
      selected={isSelected}
      ref={itemRef}
      sx={mergeSx(ITEM_SX, sx)}>
      <MenuItemContent
        icon={icon}
        label={label}
        isCritical={false}
        trailing={
          <Box component="span" aria-hidden="true" sx={CHEVRON_SX}>
            <Icons.ChevronRight />
          </Box>
        }
      />
    </MuiMenuItem>
  );
};

/**
 * A row of an `ActionsMenu` that opens a second panel beside it on click, Enter, Space or ArrowRight.
 * Picking a row in that panel closes both menus, as does a press outside; Escape and ArrowLeft close
 * only the nested panel and put focus back on this row.
 */
export const SubmenuItem = ({
  icon,
  label,
  isSelected = false,
  children,
  menuId,
  ...rest
}: SubmenuItemProps) => {
  const generatedMenuId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const closeEnclosingMenu = useActionsMenuClose();

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);
  const handleItemSelect = () => {
    setIsOpen(false);
    closeEnclosingMenu?.();
  };

  return (
    <ActionsMenu
      itemName={label}
      open={isOpen}
      onOpen={handleOpen}
      onClose={handleClose}
      placement="right-start"
      offsetTop={SUBMENU_OFFSET}
      menuId={menuId ?? `submenu-${generatedMenuId}`}
      isSubmenu
      sx={WRAPPER_SX}
      trigger={
        <SubmenuItemTrigger
          {...rest}
          icon={icon}
          label={label}
          isSelected={isSelected || isOpen}
        />
      }>
      <ActionsMenuProvider onClose={handleItemSelect}>{children}</ActionsMenuProvider>
    </ActionsMenu>
  );
};
